/* eslint-disable complexity */
import { setReleaseTrain } from '@squareup/dex-data-dex-tech-docs-view-state-slices';
import {
  followClientSideRedirects,
  loadDocPageInView,
  loadNav,
  loadDiffPageContent,
  loadReleaseTrain,
} from '@squareup/dex-store-access-dex-tech-docs-actions';
import { SliceError, StateNodeError } from '@squareup/dex-types-data-state';
import { DocsQueryParams } from '@squareup/dex-types-shared-docs';
import {
  isProductionEnvironment,
  isPublicContext,
  isServerContext,
} from '@squareup/dex-utils-environment';
import { log } from '@squareup/dex-utils-shared-logging';
import { withSqAvtPagesDataFetchMiddleware } from '@squareup/dex-utils-shared-next-pages-data-fetch-middleware';
import { runAndRecordFrameworkMetrics } from '@squareup/dex-utils-shared-server-framework-metrics';
import { GetServerSidePropsContext } from 'next';
import { v4 } from 'uuid';

import { configureApp } from './app-config';
import { findTabIdForSlug } from './left-nav-resolver';
import { resolveMdxManifestSlug, resolveMdxPageSlug } from './mdx-doc-routes';
import {
  isMdxEnabledForSlugFromPayload,
  loadMdxFlagPayload,
  readMdxFlagPayloadFromNextData,
} from './mdx-flag';
import type { MdxFlagPayload } from './mdx-flag';
import {
  buildPublishingContext,
  filterNavSet,
  resolveManifestEntry,
} from './mdx-manifest-resolver';
import {
  clientManifest,
  fetchMdxPayloadFromCdn,
  loadManifest,
  loadMdxMarkdownFromDisk,
  loadMdxPayloadFromDisk,
  nodeRequire,
} from './mdx-payload';
import { resolveMdxVariantPayloadRequest } from './mdx-payload-resolver';
import { initializeApp } from './server-app-init';
import {
  setupS2sFetch,
  setupServerLogger,
  setupServerMetrics,
} from './server-module-setup';

// Detect that we're running on a Playpen pod. /playpen is an emptyDir the
// playpen-rsync sidecar shares with the app container; it does not exist
// on regular CCD pods, so production behaviour is unchanged. The
// process-level result is stable, so we compute it once at module init
// instead of once per request.
const IS_PLAYPEN_POD: boolean = (() => {
  if (!nodeRequire) {
    return false;
  }
  try {
    const fs = nodeRequire('fs') as typeof import('fs');
    return fs.existsSync('/playpen');
  } catch {
    return false;
  }
})();

async function setupApp(ctx: GetServerSidePropsContext) {
  const requestId = v4();
  let initError: StateNodeError | undefined = undefined;
  let serverData: Awaited<ReturnType<typeof initializeApp>>['data'] = {};

  if (isServerContext()) {
    await setupServerMetrics();
    await setupS2sFetch();
    await setupServerLogger();
  }

  const {
    error: configError,
    isLoading: configIsLoading,
    store,
  } = configureApp({ requestId });

  if (!configError && !configIsLoading) {
    const serverInitProps = {
      headers: ctx.req?.headers,
      cookies: ctx.req?.cookies,
      requestId,
    };

    const initResult = await initializeApp(serverInitProps);

    initError = initResult.error;
    serverData = initResult.data;
  }

  return {
    configError,
    initError,
    store,
    serverData,
  };
}

function notFoundIfProduction(ctx: GetServerSidePropsContext) {
  if (isProductionEnvironment() && isPublicContext()) {
    if (ctx.res) {
      // eslint-disable-next-line no-param-reassign
      ctx.res.statusCode = 404;
      ctx.res.end();
    }

    return {
      responseCode: 404,
      returnVal: {},
    };
  }

  return null;
}

function isPublicProductionRequest(): boolean {
  return isProductionEnvironment() && isPublicContext();
}

function queryOverridesAllowed(): boolean {
  return !isPublicProductionRequest();
}

function notFoundDocResponse(
  ctx: GetServerSidePropsContext,
  mdxFlagPayload: MdxFlagPayload | null
) {
  if (ctx.res) {
    // eslint-disable-next-line no-param-reassign
    ctx.res.statusCode = 404;
  }

  return {
    responseCode: 404,
    returnVal: {
      data: {
        mdxFlag: mdxFlagPayload,
        doc: {
          notFound: true,
          preview: false,
        },
      },
    },
  };
}

function getMarkdownSlug(slug: string): string | null {
  return slug.endsWith('.md') ? slug.slice(0, -'.md'.length) : null;
}

function getMdxPayloadKey(slug: string): string | null {
  const prefix = '_mdx-payloads/';
  const suffix = '.json';

  if (!slug.startsWith(prefix) || !slug.endsWith(suffix)) {
    return null;
  }

  return slug.slice(prefix.length, -suffix.length);
}

// Variant payload URLs stay under `/docs/*` because that namespace is the
// public developer edge's known docs-forwarding surface. Stable payloads are
// still static files; this handler only catches private variant paths that do
// not exist under public/docs and must pass the manifest resolver first.
async function maybeServeMdxVariantPayload(
  ctx: GetServerSidePropsContext,
  slug: string
) {
  const payloadKey = getMdxPayloadKey(slug);
  if (!payloadKey || !isServerContext() || !ctx.res) {
    return null;
  }
  const res = ctx.res;

  const payload = await resolveMdxVariantPayloadRequest({
    query: {
      ...ctx.query,
      payloadKey,
    },
    allowQueryOverrides: queryOverridesAllowed(),
  });

  if (!payload) {
    res.statusCode = 404;
    res.end();
    return {
      responseCode: 404,
      returnVal: {},
    };
  }

  // `no-store` is deliberately stricter than the markdown/HTML paths below.
  // This guarded surface serves only `_variants/*` payloads, and the same URL
  // can resolve to different content per `?publishedAt` / `?visible`, so it
  // must never be cached under a query-insensitive edge key. Query overrides
  // are disabled in public prod (see `queryOverridesAllowed`) — but we keep the
  // strictest posture here as defense-in-depth for private/future payloads.
  res.setHeader('Cache-Control', 'private, no-store');
  res.setHeader('Content-Type', 'application/json; charset=utf-8');
  res.statusCode = 200;
  res.end(JSON.stringify(payload));

  return {
    responseCode: 200,
    returnVal: {},
  };
}

// Preserve the public `/docs/<slug>.md` contract while routing through the
// same manifest publishing resolver as HTML pages. Static public `.md` files
// cannot safely represent future/hidden variants because those artifacts live
// in `.generated/docs` and must only be read after the request is eligible.
async function maybeServeMdxMarkdown(
  ctx: GetServerSidePropsContext,
  slug: string,
  mdxFlagPayload: MdxFlagPayload | null
) {
  const markdownSlug = getMarkdownSlug(slug);
  if (!markdownSlug || !isServerContext() || !ctx.res) {
    return null;
  }
  if (!isMdxEnabledForSlugFromPayload(markdownSlug, mdxFlagPayload)) {
    return null;
  }

  const manifest = await loadManifest();
  const publishingContext = buildPublishingContext({
    manifest,
    query: ctx.query,
    allowQueryOverrides: queryOverridesAllowed(),
  });
  const resolved = resolveManifestEntry(
    manifest,
    markdownSlug,
    publishingContext
  );

  if (!resolved || resolved.source === 'contentful') {
    return null;
  }
  if (resolved.source === 'none') {
    return notFoundDocResponse(ctx, mdxFlagPayload);
  }

  const markdown = await loadMdxMarkdownFromDisk(resolved.markdownKey);
  if (!markdown) {
    log.info(
      `[mdx] manifest says mdx for markdown slug "${markdownSlug}" but markdown is missing; falling through to Contentful`
    );
    return null;
  }

  // Safe to edge-cache even though this URL selects a variant: query overrides
  // are off in public prod (see `queryOverridesAllowed`), so in prod the
  // response is a deterministic function of (URL, current date) — a variant
  // only resolves here after its `publishedAt` has passed, i.e. it is already
  // public. Match the SSR-page TTL. Playpen disables caching so extension
  // toggles take effect per request.
  ctx.res.setHeader(
    'Cache-Control',
    IS_PLAYPEN_POD
      ? 'no-store, no-cache, must-revalidate, max-age=0'
      : 'public, max-age=300, s-maxage=300, stale-while-revalidate=86400'
  );
  ctx.res.setHeader('Content-Type', 'text/markdown; charset=utf-8');
  // eslint-disable-next-line no-param-reassign
  ctx.res.statusCode = 200;
  ctx.res.end(markdown);

  return {
    responseCode: 200,
    returnVal: {},
  };
}

function getInitialPropsDocPage(ctx: GetServerSidePropsContext) {
  withSqAvtPagesDataFetchMiddleware(ctx);

  return runAndRecordFrameworkMetrics(
    { group: 'docs', handler: 'page' },
    async () => {
      const res = ctx.res;

      // MDX docs-as-code: if LD flag is on AND the slug is listed as "mdx" in
      // mdx/manifest.json, serve the precompiled MDX payload. Otherwise
      // fall through to the existing Contentful flow.
      //
      // The LD snapshot is resolved once on the server and stuffed into
      // pageProps as `mdxFlag`. On client-side SPA navigation we re-read
      // that same snapshot from window.__NEXT_DATA__ instead of asking LD
      // again (the LD wrapper is server-only — see next.config.js). The
      // snapshot is frozen for the SPA session; a flag flip only takes
      // effect on the user's next full page load.
      const mdxFlagPayload = isServerContext()
        ? await loadMdxFlagPayload()
        : readMdxFlagPayloadFromNextData();
      try {
        const querySlug = ctx.query['slug'];
        const slug =
          typeof querySlug === 'string'
            ? querySlug
            : Array.isArray(querySlug)
            ? querySlug.join('/')
            : '';

        const payloadResponse = await maybeServeMdxVariantPayload(ctx, slug);
        if (payloadResponse) {
          return payloadResponse;
        }

        const markdownResponse = await maybeServeMdxMarkdown(
          ctx,
          slug,
          mdxFlagPayload
        );
        if (markdownResponse) {
          return markdownResponse;
        }

        // `?mdx=no` (or `=false`) opts the request out of the MDX path so
        // the same URL falls through to Contentful. Useful while the LD
        // flag is at 100% in staging — without an override, the MDX page
        // renders for everyone and there's no way to compare against the
        // Contentful render side-by-side. `?mdx=yes` is accepted as the
        // visible switch-back value, but the LD flag still controls opt-in.
        const mdxQuery = ctx.query['mdx'];
        const mdxOptOut =
          typeof mdxQuery === 'string' &&
          (mdxQuery === 'no' || mdxQuery === 'false' || mdxQuery === '0');

        const mdxManifestSlug = resolveMdxManifestSlug(slug);

        if (
          !mdxOptOut &&
          isMdxEnabledForSlugFromPayload(mdxManifestSlug, mdxFlagPayload)
        ) {
          const manifest = isServerContext()
            ? await loadManifest()
            : clientManifest;
          const publishingContext = buildPublishingContext({
            manifest,
            query: ctx.query,
            allowQueryOverrides: queryOverridesAllowed(),
          });
          const resolved = resolveManifestEntry(
            manifest,
            mdxManifestSlug,
            publishingContext
          );

          if (resolved?.source === 'none') {
            return notFoundDocResponse(ctx, mdxFlagPayload);
          }

          if (resolved?.source === 'mdx') {
            const payload = isServerContext()
              ? await loadMdxPayloadFromDisk(resolved.payloadKey)
              : await fetchMdxPayloadFromCdn(resolved.payloadKey, {
                  publicSlug: resolved.publicSlug,
                  query: ctx.query,
                });

            if (payload) {
              const navSet = filterNavSet(payload.navSet, publishingContext, {
                manifest,
              });
              if (res) {
                // On regular CCD pods: short s-maxage (5 min) so a deploy is
                // reflected at the edge within minutes; long
                // stale-while-revalidate (24 h) keeps perceived latency low
                // while CF revalidates in the background. Earlier 86400 /
                // 604800 made deploy bugs invisible for up to a day on POPs
                // that had cached the prior HTML.
                //
                // On Playpen pods: disable CDN/browser caching entirely so
                // toggling the Playpen Chrome extension on/off shows the
                // right variant on every request. Otherwise CF caches one
                // variant for `s-maxage` and developers see stale results
                // mid-iteration. /playpen exists only on playpen pods (the
                // playpen-rsync sidecar's emptyDir).
                res.setHeader(
                  'Cache-Control',
                  IS_PLAYPEN_POD
                    ? 'no-store, no-cache, must-revalidate, max-age=0'
                    : 'public, s-maxage=300, stale-while-revalidate=86400'
                );
              }
              // Populate `pageInView.page` so DocsBasePage's leftNav memo
              // (which guards on `pageInView.page.leftNavId`) renders the
              // sidebar. We resolve leftNavId by walking navSet so a
              // single-segment slug placed under a multi-segment tab
              // (e.g. `square-get-started` → `get-started`) still picks
              // the correct tab.
              const pageSlug = resolveMdxPageSlug(slug);
              const leftNavId =
                payload.metadata.pageType === 'landing'
                  ? payload.metadata.leftNavId
                  : payload.metadata.leftNavId ||
                    findTabIdForSlug(navSet, mdxManifestSlug);
              return {
                responseCode: 200,
                returnVal: {
                  data: {
                    mdxFlag: mdxFlagPayload,
                    doc: {
                      mdxMode: true,
                      mdxSource: payload.mdxSource,
                      mdxMetadata: payload.metadata,
                      navSet: { value: navSet },
                      pageInView: {
                        page: {
                          id: `mdx:${mdxManifestSlug}`,
                          slug: pageSlug,
                          leftNavId,
                        },
                      },
                      notFound: false,
                      preview: false,
                    },
                  },
                },
              };
            }
            // Manifest said mdx but payload is missing (build out of sync).
            // Log and fall through to Contentful so users still see content.
            log.info(
              `[mdx] manifest says mdx for slug "${mdxManifestSlug}" but payload is missing; falling through to Contentful`
            );
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[mdx] Error resolving MDX payload, falling back to Contentful:',
          error
        );
      }

      const { configError, initError, store, serverData } = await setupApp(ctx);

      log.info(
        `http request: ${(ctx as unknown as { asPath: string }).asPath}`
      );

      const previousDocState = store?.getState().docsShellViewState;
      let isPreview = Boolean(ctx.query[DocsQueryParams.preview]);
      const unparsedReleaseTrain = ctx.query[DocsQueryParams.train];
      let releaseTrain =
        typeof unparsedReleaseTrain === 'string'
          ? unparsedReleaseTrain
          : unparsedReleaseTrain?.join('/') || undefined;
      let contentfulEnvironment = ctx.query[DocsQueryParams.contentfulEnv] as
        | string
        | undefined;

      // Don't do preview, release train logic, or contentful environments in production
      if (
        (isPreview || releaseTrain || contentfulEnvironment) &&
        isProductionEnvironment() &&
        isPublicContext()
      ) {
        isPreview = false;
        releaseTrain = undefined;
        contentfulEnvironment = undefined;
      }

      if (isPreview && releaseTrain) {
        const statusCode = 400;
        if (res) {
          res.statusCode = statusCode;
        }

        return {
          responseCode: statusCode,
          returnVal: {
            data: {
              ...serverData,
              doc: {
                error: {
                  error:
                    'cannot set both the "preview" and "train" query params',
                  originalStatus: statusCode,
                  status: 'CUSTOM_ERROR',
                },
              },
            },
          },
        };
      }

      if (releaseTrain && contentfulEnvironment) {
        const statusCode = 400;
        if (res) {
          res.statusCode = statusCode;
        }

        return {
          responseCode: statusCode,
          returnVal: {
            data: {
              ...serverData,
              doc: {
                error: {
                  error:
                    'cannot set both the "train" and "environment" query params',
                  originalStatus: statusCode,
                  status: 'CUSTOM_ERROR',
                },
              },
            },
          },
        };
      }

      let isRedirect = false;
      if (!configError && !initError) {
        const loadActions = [];

        // Load up the release train too
        if (releaseTrain) {
          loadActions.push(store?.dispatch(loadReleaseTrain({ releaseTrain })));
        } else {
          await store?.dispatch(setReleaseTrain({ inView: false }));
        }

        const loadWithRedirect = async () => {
          await store?.dispatch(
            loadDocPageInView({
              query: ctx.query,
              preview: isPreview,
              releaseTrain,
              contentfulEnvironment,
              cookie: ctx.req?.headers?.cookie,
            })
          );
          const page = store?.getState().docsShellViewState?.pageInView?.page;
          if (page?.type === 'redirect') {
            // If server-side, just do a 302 and don't worry about rendering
            if (isServerContext()) {
              res.writeHead(302, { Location: `/docs/${page.redirectTo}` });
              // Calling res.end ensures nextjs won't try to render the page
              res.end();
              return {
                responseCode: 302,
                returnVal: {},
              };
            } else {
              isRedirect = true;
              // If client-side, just follow the redirects as much as you can
              await store?.dispatch(
                followClientSideRedirects({
                  slug: page.redirectTo,
                  preview: isPreview,
                  releaseTrain,
                  contentfulEnvironment,
                })
              );
            }
          }

          return null;
        };

        // Run page load and nav load in parallel
        loadActions.push(loadWithRedirect());

        // Don't fetch the nav if we already have it, OR refetch if the previous nav doesn't
        // match the preview state
        if (
          !previousDocState?.navSet.value ||
          previousDocState.adminFeature.isPreview !== isPreview ||
          previousDocState.adminFeature.releaseTrain.inView !==
            Boolean(releaseTrain)
        ) {
          loadActions.push(
            store?.dispatch(
              loadNav({
                preview: isPreview,
                releaseTrain,
                contentfulEnvironment,
                cookie: ctx.req?.headers?.cookie,
              })
            )
          );
        }

        const loadResults = await Promise.all(loadActions);
        for (const result of loadResults) {
          // Handle in case the redirect occurred
          if (result?.responseCode) {
            return result;
          }
        }
      }

      const docsState = store?.getState().docsShellViewState;

      const notFound = !docsState?.pageInView.page;

      const docsError = docsState?.pageInView.error;

      // error if the release train can't be found
      const releaseTrainError =
        docsState?.adminFeature.releaseTrain.state.error;
      if (releaseTrain && releaseTrainError) {
        const statusCode = releaseTrainError.originalStatus || 404;
        if (res) {
          res.statusCode = statusCode;
        }

        return {
          responseCode: statusCode,
          returnVal: {
            data: {
              ...serverData,
              doc: {
                error: docsState?.adminFeature.releaseTrain.state.error,
              },
            },
          },
        };
      }

      if (notFound && res) {
        res.statusCode = 404;
      }

      // Only docs errors result in a status code error;
      if (docsError && res) {
        res.statusCode = docsError.originalStatus || 500;
      }

      const returnVal = {
        data: {
          ...serverData,
          mdxFlag: mdxFlagPayload,
          doc: {
            pageInView: docsState?.pageInView,
            navSet: docsState?.navSet,
            error: docsError,
            notFound,
            preview: docsState?.adminFeature.isPreview,
            releaseTrain,
            contentfulEnvironment,
            didRedirect: isRedirect,
          },
        },
      };

      return {
        responseCode: notFound ? 404 : res?.statusCode || 200,
        returnVal,
      };
    }
  );
}

/**
 * The user should never go to these pages, because they are owned by connectv2-docs
 * If they end up here via client-side navigation, we will reload the page
 * If they end up here via server-side navigation, we return a 404 since it
 * should be impossible
 */
function getInitialPropsDocsApiPage(ctx: GetServerSidePropsContext) {
  withSqAvtPagesDataFetchMiddleware(ctx);

  return runAndRecordFrameworkMetrics(
    { group: 'docs', handler: '/docs/api' },
    async () => {
      if (!isServerContext()) {
        // Force reload to reroute to connectv2-docs
        window.location.assign(
          (ctx as unknown as { asPath: string })?.asPath || '/docs'
        );
        // You can't actually await a window.location.assign, so we'll wait ~5s before giving up and 404'ing
        await new Promise((resolve) => {
          setTimeout(resolve, 5000);
        });
      }

      if (ctx.res) {
        // eslint-disable-next-line no-param-reassign
        ctx.res.statusCode = 404;
        ctx.res.end();
      }

      return {
        responseCode: 404,
        returnVal: {},
      };
    }
  );
}

function getInitialPropsDiffPage(ctx: GetServerSidePropsContext) {
  withSqAvtPagesDataFetchMiddleware(ctx);

  return runAndRecordFrameworkMetrics<unknown>(
    { group: 'docs', handler: 'diff' },
    async () => {
      const blockedResponse = notFoundIfProduction(ctx);
      if (blockedResponse) {
        return blockedResponse;
      }

      const res = ctx.res;
      const { configError, initError, store, serverData } = await setupApp(ctx);

      const isPreview = Boolean(ctx.query[DocsQueryParams.preview]);
      const releaseTrain = ctx.query[DocsQueryParams.train];
      if (isPreview || releaseTrain) {
        const statusCode = 400;
        if (res) {
          res.statusCode = statusCode;
        }

        return {
          responseCode: statusCode,
          returnVal: {
            data: {
              ...serverData,
              doc: {
                error: {
                  error:
                    'cannot set the "preview" or "train" query params on diff pages',
                  originalStatus: statusCode,
                  status: 'CUSTOM_ERROR',
                } as SliceError,
              },
            },
          },
        };
      }

      const previousDocState = store?.getState().docsShellViewState;

      let currentState;
      if (!configError && !initError) {
        await store?.dispatch(
          loadDiffPageContent({ query: ctx.query, preview: false })
        );

        currentState = store?.getState().docsShellViewState.pageInView;
        if (currentState && !currentState.error) {
          await store?.dispatch(
            loadDiffPageContent({ query: ctx.query, preview: true })
          );
        }

        // The nav will always be considered un-diffable, so just fetch the current one
        if (!previousDocState?.navSet.value) {
          await store?.dispatch(loadNav({ preview: false }));
        }
      }

      const previewDocsState = store?.getState().docsShellViewState;
      const notFound = !previewDocsState?.pageInView.page;
      const docsError = previewDocsState?.pageInView.error;

      if (notFound && res) {
        res.statusCode = 404;
      }

      // Only docs errors result in a status code error;
      if (docsError && res) {
        res.statusCode = docsError.originalStatus || 500;
      }

      const returnVal = {
        data: {
          ...serverData,
          doc: {
            pageInView: currentState,
            previewPageInView: previewDocsState?.pageInView,
            navSet: previewDocsState?.navSet,
            error: docsError,
            notFound,
          },
        },
      };

      return {
        responseCode: notFound ? 404 : res?.statusCode || 200,
        returnVal,
      };
    }
  );
}

export {
  getInitialPropsDocPage,
  getInitialPropsDiffPage,
  getInitialPropsDocsApiPage,
};
